import { useCallback, useEffect, useMemo } from 'react';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  WATERFALL_ALLOWED_PARAMS,
  WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION,
  WATERFALL_QUERY_PARAM_EXIT_DATE,
  WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE,
  WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE, WATERFALL_QUERY_PARAM_LESS_DEBT, WATERFALL_QUERY_PARAM_PLUS_CASH
} from '../constants';

export interface WaterfallUrlParams {
  company?: string;
  date?: string;
  'exit-equity-value'?: string;
  'exit-date'?: string;
  'cap-table-version'?: string;
  'plus-cash'?: string;
  'less-debt'?: string;
}

export const useWaterfallUrlParams = (isFirmPage?: boolean) => {
  const navigate = useNavigate();
  const location = useLocation();

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);

  const updateUrlParams = useCallback(
    (params: Partial<WaterfallUrlParams>, replace = true) => {
      const newQuery: Record<string, string | undefined> = { ...parsedQuery, ...params };
      
      // Remove undefined values
      Object.keys(newQuery).forEach(key => {
        if ([null, undefined, ''].includes(newQuery[key])) {
          delete newQuery[key];
        }
      });

      const search = Object.keys(newQuery).length > 0 ? `?${queryString.stringify(newQuery)}` : '';
      
      if (replace) {
        navigate({ pathname: location.pathname, search }, { replace: true });
      } else {
        navigate({ pathname: location.pathname, search });
      }
    },
    [navigate, location.pathname, parsedQuery]
  );

  const clearUrlParams = useCallback(() => {
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [navigate, location.pathname]);

  const clearWaterfallParams = useCallback(() => {
    if (!isFirmPage) {
      let hasWaterfallParams = false;
      
      // Check if any waterfall parameters exist
      WATERFALL_ALLOWED_PARAMS.forEach(param => {
        if (parsedQuery[param] !== undefined) {
          hasWaterfallParams = true;
        }
      });
      
      if (hasWaterfallParams) {
        // Create clean query object without waterfall parameters
        const cleanQuery = { ...parsedQuery };
        WATERFALL_ALLOWED_PARAMS.forEach(param => {
          delete cleanQuery[param];
        });
        
        const search = Object.keys(cleanQuery).length > 0
          ? `?${queryString.stringify(cleanQuery)}`
          : '';
        
        navigate({ 
          pathname: location.pathname, 
          search 
        }, { replace: true });
      }
    }
  }, [isFirmPage, navigate, location.pathname, parsedQuery]);

  // Clear non-waterfall parameters when component mounts for company pages
  useEffect(() => {
    if (!isFirmPage) {
      const unwantedParams: string[] = [];
      
      // Find parameters not in the waterfall allowed list
      Object.keys(parsedQuery).forEach(key => {
        if (!WATERFALL_ALLOWED_PARAMS.includes(key)) {
          unwantedParams.push(key);
        }
      });
      
      if (unwantedParams.length > 0) {
        // Create clean query object without unwanted parameters
        const cleanQuery = { ...parsedQuery };
        unwantedParams.forEach(param => {
          delete cleanQuery[param];
        });
        
        const search = Object.keys(cleanQuery).length > 0 ? `?${queryString.stringify(cleanQuery)}` : '';
        
        // Navigate to clean URL without triggering a page reload
        navigate({ 
          pathname: location.pathname, 
          search 
        }, { replace: true });
      }
    }
  }, [isFirmPage, navigate, location.pathname, parsedQuery]);

  const getUrlParams = useCallback(() => ({
    company: parsedQuery.company as string | undefined,
    date: parsedQuery.date as string | undefined,
    [WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]: parsedQuery[WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE] as string | undefined,
    [WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]: parsedQuery[WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE] as string | undefined,
    [WATERFALL_QUERY_PARAM_PLUS_CASH]: parsedQuery[WATERFALL_QUERY_PARAM_PLUS_CASH] as string | undefined,
    [WATERFALL_QUERY_PARAM_LESS_DEBT]: parsedQuery[WATERFALL_QUERY_PARAM_LESS_DEBT] as string | undefined,
    [WATERFALL_QUERY_PARAM_EXIT_DATE]: parsedQuery[WATERFALL_QUERY_PARAM_EXIT_DATE] as string | undefined,
    [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: parsedQuery[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION] as string | undefined,
  }), [parsedQuery]);

  return {
    urlParams: getUrlParams(),
    updateUrlParams,
    clearUrlParams,
    clearWaterfallParams,
    parsedQuery,
  };
};
